import Vue from "vue";

export type MessageType = "success" | "info" | "warning" | "error";

// The following is responsible for the inline messages that may be presented to the user.
// If the message property is null then no message will be shown to the user.
// The type can be one of the following strings:
// 'success'
// 'info'
// 'warning'
// 'error'
export interface InlineMessage {
  message: string | null;
  type: MessageType;
}
export type ErrorHandlingOverride = {
  [statusCode: string]: {
    errorMessage?: string | null | undefined;
    type?: MessageType | null | undefined;
  };
};

export default Vue.extend({
  data: function() {
    return {
      inlineMessage: {
        message: null,
        type: "error"
      } as InlineMessage,
      // The following will control whether the controls on screen are disabled while we are
      // processing and error states associated with active work
      processing: false
    };
  },

  methods: {
    generateErrorInformation(
      error: Error | undefined,
      defaultErrorMessage: string | undefined = undefined,
      overrides: ErrorHandlingOverride | null = null
    ): { message: string; type: MessageType } {
      if (!defaultErrorMessage?.length)
        defaultErrorMessage =
          "An unexpected error occurred. Your network may be having issues. Please try again later.";
      if (!error) return { message: defaultErrorMessage, type: "error" };

      let parsedError = error as any;
      let errorMessage = parsedError.message;
      let type = "error" as MessageType;
      switch (parsedError.statusCode) {
        case 401: {
          errorMessage = "You must login to perform this action.";
          break;
        }
        case 403: {
          errorMessage = "You do not have permission to perform this action.";
          break;
        }
        case 404: {
          errorMessage = "This item could not be found.";
          break;
        }
        case 500:
        case 504: {
          errorMessage = parsedError.message ?? "";
          break;
        }
        default: {
          if (!!parsedError.message) {
            errorMessage = parsedError.message;
          } else {
            // empty out the errorMessage so it uses the default
            errorMessage = "";
          }
          break;
        }
      }

      if (!errorMessage.length) errorMessage = defaultErrorMessage;

      var override = !!overrides ? overrides[`${parsedError.statusCode}`] : null;
      if (!!override) {
        if (!!override.errorMessage) errorMessage = override.errorMessage;
        if (!!override.type) type = override.type;
      }

      return { message: errorMessage, type: type };
    },
    // Unhandled server errors come back as a 500.  The `error500Message` is the default message to show when the server hasn't returned its own
    handleError(
      error: any | unknown,
      defaultErrorMessage: string | undefined = undefined,
      overrides: ErrorHandlingOverride | null = null
    ) {
      var messageInfo = this.generateErrorInformation(
        error as Error | undefined,
        defaultErrorMessage,
        overrides
      );
      this.inlineMessage.message = messageInfo.message;
      this.inlineMessage.type = messageInfo.type;
    }
  }
});

